<template>
  <aside class="lib-panel">
    <div class="lib-panel__root-row">
      <el-select
        class="lib-panel__root"
        :value="rootId"
        placeholder="Chọn danh mục gốc"
        :loading="rootsLoading"
        no-data-text="Chưa có danh mục"
        @change="selectRoot"
      >
        <template #prefix><i class="el-icon-files lib-panel__root-icon"></i></template>
        <el-option v-for="root in roots" :key="root._id" :label="root.name" :value="root._id">
          <span>{{ root.name }}</span>
          <span class="lib-panel__count lib-panel__count--right">{{ root.itemCount }}</span>
        </el-option>
      </el-select>
      <el-dropdown v-if="editable" trigger="click" @command="handleRootCommand">
        <el-button class="lib-panel__root-more" plain icon="el-icon-more" title="Danh mục gốc" />
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="add" icon="el-icon-plus">Thêm danh mục gốc</el-dropdown-item>
          <el-dropdown-item v-if="currentRoot" command="edit" icon="el-icon-edit">Sửa danh mục gốc</el-dropdown-item>
          <el-dropdown-item v-if="currentRoot" command="delete" icon="el-icon-delete" divided>Xoá danh mục gốc</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <el-input
      v-model="search"
      class="lib-panel__search"
      prefix-icon="el-icon-search"
      placeholder="Tìm danh mục..."
      clearable
    />

    <div class="lib-panel__tabs">
      <button
        v-for="tab in ownerTabs"
        :key="tab.name"
        type="button"
        :class="['lib-panel__tab', { 'is-active': tab.name === ownerCategory }]"
        @click="$emit('owner-change', tab.name)"
      >
        {{ tab.label }}
      </button>
    </div>

    <div class="lib-panel__body">
      <div v-if="search.trim()" v-loading="searchLoading" class="lib-panel__results">
        <div
          v-for="cat in searchResults"
          :key="cat._id"
          :class="['lib-panel__result', { 'is-active': cat._id === categoryId }]"
          @click="selectSearchResult(cat)"
        >
          <div class="lib-panel__result-name">
            <i class="el-icon-folder"></i>
            <span>{{ cat.name }}</span>
            <span class="lib-panel__count">{{ cat.itemCount }}</span>
          </div>
          <div class="lib-panel__result-path">{{ cat.path.map((p) => p.name).join(' › ') }}</div>
        </div>
        <div v-if="!searchLoading && !searchResults.length" class="lib-panel__empty">Không tìm thấy danh mục</div>
      </div>

      <el-tree
        v-else-if="rootId"
        ref="tree"
        :key="treeKey"
        class="lib-panel__tree"
        node-key="_id"
        lazy
        :load="loadNode"
        :props="treeProps"
        :default-expanded-keys="expandedKeys"
        :current-node-key="categoryId"
        :expand-on-click-node="false"
        highlight-current
        @node-click="(data) => $emit('select', data._id)"
        @node-expand="(data) => toggleExpanded(data._id, true)"
        @node-collapse="(data) => toggleExpanded(data._id, false)"
      >
        <div slot-scope="{ data }" class="lib-panel__node">
          <i class="el-icon-folder lib-panel__node-icon"></i>
          <span class="lib-panel__node-name" :title="data.name">{{ data.name }}</span>
          <span v-if="editable" class="lib-panel__node-actions" @click.stop>
            <i class="el-icon-plus" title="Thêm danh mục con" @click="$refs.categoryDialog.openAdd(data._id)"></i>
            <i class="el-icon-edit" title="Sửa" @click="$refs.categoryDialog.openEdit(data)"></i>
            <i class="el-icon-delete" title="Xoá" @click="deleteCategory(data)"></i>
          </span>
          <span class="lib-panel__count">{{ data.itemCount }}</span>
        </div>
      </el-tree>

      <div v-else-if="!rootsLoading" class="lib-panel__empty">Chưa có danh mục</div>
    </div>

    <el-button v-if="editable" class="lib-panel__new" plain icon="el-icon-plus" @click="addCategory">
      Danh mục mới
    </el-button>

    <CategoryDialog ref="categoryDialog" :ownerCategory="ownerCategory" @saved="handleSaved" />
  </aside>
</template>

<script>
import debounce from 'lodash/debounce'
import examVaultApi from '@/api/exam-vault'
import CategoryDialog from '../components/CategoryDialog.vue'

export default {
  name: 'LibraryCategoryPanel',
  components: { CategoryDialog },

  props: {
    ownerCategory: { type: String, required: true },
    ownerTabs: { type: Array, required: true },
    rootId: { type: String, default: null },
    categoryId: { type: String, default: null },
    editable: { type: Boolean, default: false }
  },

  data() {
    return {
      roots: [],
      rootsLoading: false,
      search: '',
      searchResults: [],
      searchLoading: false,
      treeKey: 0,
      expandedKeys: [],
      // Nhánh cần mở khi cây (lazy) chưa tải tới
      pendingPath: null,
      treeProps: {
        label: 'name',
        isLeaf: (data) => !data.childCount
      }
    }
  },

  computed: {
    currentRoot() {
      return this.roots.find((r) => r._id === this.rootId) || null
    }
  },

  watch: {
    ownerCategory: {
      handler() {
        this.search = ''
        this.expandedKeys = []
        this.fetchRoots()
      },
      immediate: true
    },
    rootId() {
      this.expandedKeys = []
      this.treeKey++
    },
    categoryId(id) {
      if (this.$refs.tree) this.$refs.tree.setCurrentKey(id)
    },
    search() {
      this.searchLoading = !!this.search.trim()
      this.debouncedSearch()
    }
  },

  created() {
    this.debouncedSearch = debounce(this.runSearch, 300)
  },

  methods: {
    async fetchRoots() {
      const owner = this.ownerCategory
      this.rootsLoading = true
      try {
        const res = await examVaultApi.listLibraryCategories({ ownerCategory: owner })
        if (owner !== this.ownerCategory) return
        this.roots = res.data.list || []
        const keep = this.roots.find((r) => r._id === this.rootId)
        if (!keep) {
          this.$emit('select-root', this.roots[0] ? this.roots[0]._id : null)
        }
      } finally {
        this.rootsLoading = false
      }
    },

    selectRoot(rootId) {
      this.$emit('select-root', rootId)
    },

    async loadNode(node, resolve) {
      const parentId = node.level === 0 ? this.rootId : node.data._id
      if (!parentId) return resolve([])
      try {
        const res = await examVaultApi.listLibraryCategories({ ownerCategory: this.ownerCategory, parentId })
        resolve(res.data.list || [])
        if (node.level === 0 && this.pendingPath) this.$nextTick(() => this.revealPath(this.pendingPath))
      } catch (e) {
        resolve([])
      }
    },

    // path = gốc → danh mục; gốc là level 0 của cây nên mở từ phần tử thứ 2
    revealPath(path) {
      const tree = this.$refs.tree
      if (!path || !path.length || String(path[0]._id) !== String(this.rootId)) return
      this.pendingPath = path
      if (!tree) return
      const target = path[path.length - 1]._id
      const ancestors = path.slice(1, -1).map((p) => p._id)
      const step = (i) => {
        if (i >= ancestors.length) {
          tree.setCurrentKey(target)
          this.pendingPath = null
          return
        }
        const node = tree.getNode(ancestors[i])
        if (!node) return
        if (!this.expandedKeys.includes(node.data._id)) this.expandedKeys.push(node.data._id)
        node.expand(() => this.$nextTick(() => step(i + 1)))
      }
      step(0)
    },

    toggleExpanded(id, expanded) {
      const keys = this.expandedKeys.filter((k) => k !== id)
      this.expandedKeys = expanded ? keys.concat(id) : keys
    },

    async runSearch() {
      const search = this.search.trim()
      if (!search) {
        this.searchResults = []
        this.searchLoading = false
        return
      }
      try {
        const res = await examVaultApi.searchLibraryCategories({ ownerCategory: this.ownerCategory, search })
        if (search === this.search.trim()) this.searchResults = res.data.list || []
      } finally {
        if (search === this.search.trim()) this.searchLoading = false
      }
    },

    selectSearchResult(cat) {
      const rootId = cat.path[0] && cat.path[0]._id
      if (rootId && rootId !== this.rootId) this.$emit('select-root', rootId, cat._id)
      else this.$emit('select', cat._id)
    },

    handleRootCommand(command) {
      if (command === 'add') this.$refs.categoryDialog.openAdd(null)
      else if (command === 'edit') this.$refs.categoryDialog.openEdit(this.currentRoot)
      else if (command === 'delete') this.deleteCategory(this.currentRoot)
    },

    addCategory() {
      // Thêm vào danh mục đang chọn; chưa chọn gì thì tạo danh mục gốc
      this.$refs.categoryDialog.openAdd(this.categoryId || null)
    },

    async deleteCategory(data) {
      try {
        await this.$confirm(this.$t('DELETE_CATEGORY_CONFIRMATION'), this.$t('WARNING'), {
          confirmButtonText: this.$t('CONFIRM'),
          cancelButtonText: this.$t('CANCEL'),
          type: 'warning'
        })
      } catch (e) {
        return
      }
      await examVaultApi.deleteCategory(data._id)
      this.$message.success(this.$t('CATEGORY_DELETED_SUCCESSFULLY'))
      if (data._id === this.rootId) this.$emit('select-root', null)
      else if (data._id === this.categoryId) this.$emit('select', data.parentId || this.rootId)
      this.refresh()
    },

    handleSaved() {
      this.refresh()
    },

    // Tải lại gốc + cây (giữ nhánh đang mở) để cập nhật số lượng
    refresh() {
      this.fetchRoots()
      this.treeKey++
      if (this.search.trim()) this.runSearch()
    }
  }
}
</script>

<style lang="scss" scoped>
$green: #1cab36;

.lib-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
  background: #fff;
  border: 1px solid #e8ebe9;
  border-radius: 12px;
  min-height: 0;

  &__root-row {
    display: flex;
    gap: 8px;
  }

  &__root-more {
    height: 42px;
    padding: 0 12px;
  }

  &__root {
    flex: 1;
    min-width: 0;

    ::v-deep .el-input__inner {
      height: 42px;
      background: #eef8f0;
      border-color: $green;
      color: $green;
      font-weight: 600;
    }
  }

  &__root-icon {
    color: $green;
    line-height: 42px;
    margin-left: 4px;
  }

  &__tabs {
    display: flex;
    padding: 4px;
    background: #f2f4f3;
    border-radius: 8px;
  }

  &__tab {
    flex: 1;
    border: 0;
    background: transparent;
    padding: 7px 0;
    border-radius: 6px;
    color: #6b7280;
    font-size: 13px;
    cursor: pointer;

    &.is-active {
      background: #fff;
      color: #111827;
      font-weight: 600;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }
  }

  &__body {
    flex: 1;
    min-height: 200px;
    overflow: auto;
  }

  &__tree {
    ::v-deep .el-tree-node__content {
      height: 38px;
      border-radius: 8px;
    }

    ::v-deep .el-tree-node.is-current > .el-tree-node__content {
      background: #e8f6eb;
      color: $green;
      font-weight: 600;
    }
  }

  &__node {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding-right: 8px;
    font-size: 14px;

    &:hover .lib-panel__node-actions {
      display: inline-flex;
    }
  }

  &__node-icon {
    color: #6b7280;
  }

  &__node-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__node-actions {
    display: none;
    gap: 8px;
    color: #6b7280;

    i:hover {
      color: $green;
    }
  }

  &__count {
    min-width: 24px;
    padding: 1px 6px;
    border-radius: 10px;
    background: #f2f4f3;
    color: #6b7280;
    font-size: 11px;
    font-weight: 500;
    text-align: center;

    &--right {
      float: right;
      margin-top: 8px;
      line-height: 16px;
    }
  }

  &__result {
    padding: 8px 10px;
    border-radius: 8px;
    cursor: pointer;

    &:hover,
    &.is-active {
      background: #e8f6eb;
    }
  }

  &__result-name {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;

    span:nth-child(2) {
      flex: 1;
    }
  }

  &__result-path {
    margin: 2px 0 0 22px;
    color: #9ca3af;
    font-size: 12px;
  }

  &__empty {
    padding: 24px 0;
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
  }

  &__new {
    width: 100%;
    color: $green;
    border-color: $green;
    font-weight: 600;
  }
}
</style>
